import { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'motion/react';
import { useTranslation } from 'react-i18next';
import { SplitWords } from './SplitWords';
import { EASE } from '../lib/motion';

const steps = ['one', 'two', 'three', 'four'] as const;

/** Each card gets its own tint so the stack reads as distinct layers. */
const tints = [
  'from-accent-deep/45 via-raised/60 to-raised/40',
  'from-raised-2/80 via-accent-deep/30 to-raised/40',
  'from-accent/20 via-raised/60 to-raised-2/50',
  'from-accent-deep/60 via-accent-deep/20 to-raised/50',
];

function StepCard({
  step,
  i,
  progress,
}: {
  step: (typeof steps)[number];
  i: number;
  progress: MotionValue<number>;
}) {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const n = steps.length;
  // Earlier cards recede as later ones stack on top of them.
  const scale = useTransform(
    progress,
    [i / n, 1],
    [1, reduce ? 1 : 1 - (n - i) * 0.035]
  );
  const dim = useTransform(
    progress,
    [i / n, 1],
    [0, reduce ? 0 : (n - 1 - i) * 0.18]
  );

  return (
    <div
      className="sticky mb-[14vh] last:mb-0"
      style={{ top: `calc(7rem + ${i * 1.75}rem)` }}
    >
      <motion.article
        style={{ scale }}
        className={`relative origin-top overflow-hidden rounded-2xl border border-line-strong bg-gradient-to-br ${tints[i]} p-8 shadow-[0_30px_80px_-30px_rgba(3,8,6,0.8)] backdrop-blur-2xl md:p-12`}
      >
        <div className="flex min-h-[38vh] flex-col justify-between gap-10 md:min-h-[42vh]">
          <span className="font-mono text-6xl font-medium tracking-[-0.06em] text-accent md:text-8xl">
            {t(`process.steps.${step}.index`)}
          </span>
          <div>
            <h3 className="font-display text-3xl font-semibold tracking-[-0.03em] md:text-5xl">
              {t(`process.steps.${step}.title`)}
            </h3>
            <p className="mt-4 max-w-[46ch] text-[16px] leading-relaxed text-fg/75 md:text-lg">
              {t(`process.steps.${step}.body`)}
            </p>
          </div>
        </div>
        <motion.div
          aria-hidden
          style={{ opacity: dim }}
          className="pointer-events-none absolute inset-0 bg-base"
        />
      </motion.article>
    </div>
  );
}

/**
 * Sticky-stack: the title stays pinned on the left while four glass cards pile
 * up on the right. The 3D core sits behind the stack, so it shows through the
 * frosted cards and changes as each layer lands.
 */
export function Process() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const stackRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: stackRef,
    offset: ['start start', 'end end'],
  });

  return (
    <section id="process" className="relative px-5 py-28 md:px-8 md:py-44">
      <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <h2 className="font-display text-5xl font-semibold leading-[1] tracking-[-0.04em] md:text-7xl">
              <SplitWords text={t('process.title')} />
            </h2>
            <motion.p
              initial={reduce ? false : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.8 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
              className="mt-6 max-w-[40ch] text-lg text-muted"
            >
              {t('process.lead')}
            </motion.p>
          </div>
        </div>

        <div ref={stackRef} className="lg:col-span-7">
          {steps.map((step, i) => (
            <StepCard key={step} step={step} i={i} progress={scrollYProgress} />
          ))}
        </div>
      </div>
    </section>
  );
}
